<template>
  <div class="create-tenant-project">
    <TMSHeader
      :title="$t('tenantProjects.createNewProject')"
      :buttons="showBtns"
      :status="status"
    />
    <div class="container">
      <ProjectBasicInformation
        ref="proj-basic-infor"
        :editable="true"
      />
      <hr>
      <TenantRepresentative
        ref="tenant-representative"
        :editable="true"
      />
    </div>
    <TermsAndConditions
      v-if="canAccessAllProjects"
      ref="tncTenantProjectEditPage"
      modalId="tncTenantProjectEditPage"
      :acceptCallback="termsCondtionsCallback"
    />
    <ActionConfirmModal
      ref="submitConfirmModal"
      :modalId="submitModalId"
      :confirmTitle="$t('confirmSubmit')"
    />
    <Modal
      ref="createDiffTenancyInfo"
      :showCloseBtn="false"
      modalId="createDiffTenancyInfo"
    >
      <div slot="content">
        <p class="font-bold">
          {{ $t('tenantProjects.diffTenancyInfoHint') }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="closeDiffTenancyInfoModal()"
        >{{ $t('action.ok') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'

import ProjectBasicInformation from './ProjectBasicInformation'
import TMSHeader from '@/controls/TMSHeader'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Modal from '@/controls/Modal'
import TenantRepresentative from './TenantRepresentative'
import TermsAndConditions from '@/pages/tenants/TermsAndConditions'

import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import { ActionTypes } from '@/utils/consts'
import { tenantProjectStatus, typeSubsidiaryMap } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'
import { loadTenantProject as loadTenantProjectDB, updateAndNotify } from '@/api/tenantProjects/request'
import { buildSubmitRequestBody } from '@/api/tenantProjects/util'

export default {
  name: 'CreateTenantProject',
  components: {
    TMSHeader,
    TenantRepresentative,
    TermsAndConditions,
    ProjectBasicInformation,
    ActionConfirmModal,
    Modal,
  },
  data () {
    return {
      submitModalId: `tenantProjEditSubmit-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      buttons: [{
        title: i18n.t('action.submit'),
        cls: 'btn--orange',
        func: this.openSubmitConfirm,
      }],
      tenantProjectId: this.$router.currentRoute.params.id,
      isNewProject: this.$router.currentRoute.query.isNewProject,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isTenant', 'isEngineer']),
    ...mapGetters('ccd', ['currentTenancy', 'canAccessAllProjects']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    status () {
      return lodash.get(this.currentTenantProject, 'status', tenantProjectStatus.DRAFT)
    },
    isAcceptTNC () {
      return lodash.get(this.currentTenantProject, 'tncAcceptance.tenantUser', false)
    },
    showBtns () {
      const showSaveBtn = [
        {
          title: i18n.t('action.save'),
          cls: 'btn--darkblue',
          func: this.save,
        },
        {
          title: i18n.t('action.submit'),
          cls: 'btn--orange',
          func: this.openSubmitConfirm,
        },
      ]
      return this.isEngineer ? showSaveBtn : this.buttons
    },
  },
  async mounted () {
    if (!this.isNewProject) {
      await this.loadTenantProject(this.tenantProjectId)
    } else {
      const newProject = {
        id: this.tenantProjectId,
        tenancy: {company: {name: this.$router.currentRoute.query.companyName}},
        tenancyId: this.$router.currentRoute.query.tenancyId,
        status: tenantProjectStatus.DRAFT,
        typeSubsidiary: typeSubsidiaryMap.businessAsUsual,
      }
      this.changeCurrentProject(newProject)
    }
    if (lodash.isEmpty(this.currentTenancyFloors)) {
      await this.loadTenancy(lodash.get(this.currentTenantProject, 'tenancyId'))
    }
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldByName: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_NAME,
    }),
    ...mapActions('ccd', ['loadTenancy']),
    ...mapActions('tenantProject', ['loadTenantProject', 'createTenantProject', 'changeCurrentProject']),
    async isDifferentTenancyInfo () {
      const tenantProject = await loadTenantProjectDB(lodash.get(this.currentTenantProject, 'id'))
      return lodash.isEqual(lodash.get(tenantProject, 'tenancy.premisesDetailList'), lodash.get(this.currentTenantProject, 'tenancy.premisesDetailList'))
    },
    async openDiffTenancyInfoModal () {
      await this.$refs['createDiffTenancyInfo'].open()
    },
    closeDiffTenancyInfoModal () {
      this.$refs['createDiffTenancyInfo'].close()
    },
    async save () {
      formUtils.resetDirty()
      if (this.isNewProject) {
        delete this.currentTenantProject.id
        const response = await this.createTenantProject({
          currentTenantProject: this.currentTenantProject,
          isAccept: this.$router.currentRoute.query.isAccept,
        })
        this.tenantProjectId = response.id
        await this.loadTenantProject(this.tenantProjectId)
        this.gotoTenantProjectDetailPage()
      }
    },
    async openSubmitConfirm () {
      const validateBasicInfo = this.$refs['proj-basic-infor'].validate()
      const validateAccounts = this.$refs['tenant-representative'].validate()
      if (validateBasicInfo && validateAccounts) {
        if (!this.isNewProject) {
          if (await this.isDifferentTenancyInfo()) {
            this.$refs.submitConfirmModal.open(this.beforeSubmit)
          } else {
            await this.openDiffTenancyInfoModal()
          }
        } else {
          this.$refs.submitConfirmModal.open(this.beforeSubmit)
        }
      }
    },
    async beforeSubmit () {
      if (this.isEngineer || this.$router.currentRoute.query.isAccept) {
        await this.submit()
      } else {
        this.$refs.tncTenantProjectEditPage.openModal()
      }
    },
    async termsCondtionsCallback (isAccept) {
      if (isAccept) {
        await this.submit()
      }
    },
    async submit () {
      if (this.isNewProject) {
        delete this.currentTenantProject.id
        const response = await this.createTenantProject({
          currentTenantProject: this.currentTenantProject,
          isAccept: this.$router.currentRoute.query.isAccept,
        })
        this.tenantProjectId = response.id
        await this.loadTenantProject(this.tenantProjectId)
      }
      formUtils.resetDirty()
      const request = await buildSubmitRequestBody(this.currentTenantProject)
      if (request) {
        await updateAndNotify(
          this.tenantProjectId,
          request,
          ActionTypes.tenantProjects.submit)
        this.gotoTenantProjectDetailPage()
      }
    },
    gotoTenantProjectDetailPage () {
      this.$router.replace({
        name: this.isTenant ? 'tenantProjectDetail' : 'tenantProject',
        params: {
          id: this.tenantProjectId,
          invitePopupChecking: this.isTenant,
        },
      })
    },
  },
}
</script>

<style lang='scss'>
.create-tenant-project {
  background: #e8e8e8;
  hr {
    max-width: 1440px;
    margin: 0 auto;
  }
  .multiselect {
    height: 40px;
  }
  .basic-information {
    padding-top: 20px;
    padding-left: 35px;
    .information {
      margin-left: 15px;
    }
  }
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
    box-shadow: none;
    .content {
      max-width: 1440px;
      margin: 0 auto;
    }
  }

  .container {
    background: #e8e8e8;
    padding: 20px;
    max-width: 1440px;
    margin: 0 auto;
  }
}
</style>
